import once from '../helpers/once';
import observer from '../libs/observer';
import coverModes from '../modules/cover-modes';

const instance = {};

let defaults = {
        mergePanelContainerAttr: 'data-merge-panel',
        mergePanelViewAttr: 'data-merge-panel-view',
        mergePanelOpenAttr: 'data-merge-panel-open',
        mergePanelAddPersonAttr: 'data-merge-panel-add-person',
        mergePanelCloseAttr: 'data-close',
        mergePanelCloseConfirmationAttr: 'data-close-confirmation',
        mergePanelExpandAttr: 'data-expand',
        mergePanelMinimizeAttr: 'data-minimize',
        mergePanelMergeAttr: 'data-merge',
        mergePanelLoaderLayerAttr: 'data-loader-layer',
        personTilesListAttr: 'data-person-tile-list',
        personTileAttr: 'data-person-tile',
        personTileRemoveAttr: 'data-person-tile-remove',
        personTileSelectedClass: 'selected',
        personTileFadeOutClass: 'fade-out',
        mergeButtonActiveClass: 'button--color-red',
        loadingClass: 'loading',
        selectedClass: 'selected',
        moveToBodyEnd: true,
        forcedDelay: 400,
        mergePanelPlaceholderClass: 'merge-panel-placeholder',
        mergePanelPlaceholderOpenClass: 'expand',
        panelPlaceholderOptionAttr: 'data-use-panel-placeholder',
        usePanelPlaceholder: false
    },
    eventsCache = null,
    settings,
    mergePanel,
    personTilesList,
    mergeButton,
    personIDs = [],
    loaderLayer,
    panelPlaceholder,

    createPanelPlaceholder = () => {
        panelPlaceholder = document.body.appendChild(document.createElement('div'));
        panelPlaceholder.classList.add(settings.mergePanelPlaceholderClass);
    },

    removePanelPlaceholder = () => {
        document.body.removeChild(panelPlaceholder);
    },

    clearPersonList = () => {
        personTilesList.querySelectorAll('li')
            .forEach((li) => {
                if (li.querySelector('[' + settings.personTileAttr + ']')) {
                    personTilesList.removeChild(li);
                }
            });

        // Deselect all triggers
        let triggers = document.querySelectorAll('[' + settings.mergePanelAddPersonAttr + ']');
        for (let i = triggers.length; i--;) {
            triggers[i].classList.remove(settings.selectedClass);
        }
    },

    setMergeButtonState = () => {
        let tiles = Array.from(personTilesList.querySelectorAll('[' + settings.personTileAttr + ']')),
            isSelected = tiles.some((tile) => tile.classList.contains(settings.personTileSelectedClass));

        // Check if merge-button can be activated.
        // At least two person-tiles need to be in list.
        if (tiles.length >= 2 && isSelected) {

            // Enable
            mergeButton.disabled = false;
            mergeButton.classList.add(settings.mergeButtonActiveClass);
        } else {

            // Disable
            mergeButton.disabled = true;
            mergeButton.classList.remove(settings.mergeButtonActiveClass);
        }
    },

    removePersonTile = (tile) => {
        let idToRemove = tile.getAttribute(settings.personTileAttr),
            trigger;

        once(tile, 'transitionend', () => {

            // Remove listItem which holds the tile
            personTilesList.removeChild(tile.closest('li'));
            personIDs = personIDs.filter((pid) => pid !== idToRemove);

            trigger = document.querySelector('[' + settings.mergePanelAddPersonAttr + '="' + idToRemove + '"]');
            trigger.classList.remove(settings.selectedClass);

            setMergeButtonState();
        });

        // First fade out tile
        tile.classList.add(settings.personTileFadeOutClass);
    },

    personTilesHandler = (e) => {
        let tile,
            tiles;

        // Remove personTile?
        if (e.target.closest('[' + settings.personTileRemoveAttr + ']')) {
            removePersonTile(e.target.closest('[' + settings.personTileAttr + ']'));

            e.preventDefault();

            return;
        }

        // Handle tiles selection
        if (tile = e.target.closest('[' + settings.personTileAttr + ']')) {
            tiles = personTilesList.querySelectorAll('[' + settings.personTileAttr + ']');

            // Deselect all tiles
            tiles.forEach((currentTile) => currentTile.classList.remove(settings.personTileSelectedClass));

            // Select tile
            tile.classList.add(settings.personTileSelectedClass);

            setMergeButtonState();
        }
    },

    toBodyEnd = () => {
        if (settings.moveToBodyEnd) {
            document.body.appendChild(mergePanel);
        }
    },

    delegateHandler = (e) => {

        // Expand panel (to large view)
        if (e.target.closest('[' + settings.mergePanelExpandAttr + ']')) {
            mergePanel.setAttribute(settings.mergePanelViewAttr, 'large');

            return;
        }

        // Minimize panel (to small view)
        if (e.target.closest('[' + settings.mergePanelMinimizeAttr + ']')) {
            mergePanel.setAttribute(settings.mergePanelViewAttr, 'small');
            panelPlaceholder && panelPlaceholder.classList.add(settings.mergePanelPlaceholderOpenClass);

            return;
        }

        // Close panel
        if (e.target.closest('[' + settings.mergePanelCloseAttr + ']')) {

            // Reset merge panel when it's closed
            once(mergePanel, 'transitionend', () => {
                clearPersonList();
                personIDs = [];
            });

            mergePanel.setAttribute(settings.mergePanelViewAttr, 'closed');
            panelPlaceholder && panelPlaceholder.classList.remove(settings.mergePanelPlaceholderOpenClass);

            return;
        }

        // Close panel when in confirmation view
        if (e.target.closest('[' + settings.mergePanelCloseConfirmationAttr + ']')) {

            // Reset merge panel when it's closed
            once(mergePanel, 'transitionend', () => {
                clearPersonList();
                personIDs = [];
            });

            mergePanel.setAttribute(settings.mergePanelViewAttr, 'closed-confirmation');
            panelPlaceholder && panelPlaceholder.classList.remove(settings.mergePanelPlaceholderOpenClass);

            return;
        }

        // Start Merge
        if (e.target.closest('[' + settings.mergePanelMergeAttr + ']')) {
            let targetId = getTargetId(),
                result1 = [],
                result2 = [],
                personIDSort;

            loaderLayer.classList.add(settings.loadingClass);

            // sort person id with target-person on index zero
            for (let i = 0; i < personIDs.length; i++) {
                if (personIDs[i] === targetId) {
                    result1.push(personIDs[i]);
                } else {
                    result2.push(personIDs[i]);
                }
            }
            personIDSort = result1.concat(result2);

            setTimeout(() => instance.trigger('merge', personIDSort), settings.forcedDelay);
        }
    },

    getTargetId = () => {
        let selTile = personTilesList.querySelector('.' + settings.selectedClass + '[' + settings.personTileAttr + ']');
        return selTile.getAttribute('data-person-tile');
    },

    openAddHandler = (e) => {
        let target;

        if (target = e.target.closest('[' + settings.mergePanelAddPersonAttr + ']')) {
            mergePanel.setAttribute(settings.mergePanelViewAttr, 'small');
            panelPlaceholder && panelPlaceholder.classList.add(settings.mergePanelPlaceholderOpenClass);

            instance.trigger('mergeAddPerson', target.getAttribute(settings.mergePanelAddPersonAttr));
        }
    },

    bindEvents = () => {
        mergePanel.addEventListener('click', delegateHandler);
        personTilesList.addEventListener('click', personTilesHandler);
        document.addEventListener('click', openAddHandler);
    },

    unbindEvents = () => {
        mergePanel.removeEventListener('click', delegateHandler);
        personTilesList.removeEventListener('click', personTilesHandler);
        document.removeEventListener('click', openAddHandler);
    };

instance.init = (options) => {
    settings = Object.assign({}, defaults, options);

    mergePanel = document.querySelector('[' + settings.mergePanelContainerAttr + ']');

    if (!mergePanel) {
        return;
    }

    // Set options from data-attributes
    if (mergePanel.hasAttribute(settings.panelPlaceholderOptionAttr)) {
        settings.usePanelPlaceholder = true;
    }

    // Cache previously registered events.
    if (instance.hasOwnProperty('_getEvents')) {
        eventsCache = instance._getEvents();
    }

    // Add composites
    Object.assign(instance, observer());

    // Restore previously registered events?
    if (eventsCache) {
        for (let e in eventsCache) {
            if (eventsCache.hasOwnProperty(e)) {
                for (let i = eventsCache[e].queue.length; i--; ) {
                    instance.on(e, eventsCache[e].queue[i].listener);
                }
            }
        }

        eventsCache = null;
    }

    // Cache elements
    personTilesList = document.querySelector('[' + settings.personTilesListAttr + ']');
    mergeButton = mergePanel.querySelector('[' + settings.mergePanelMergeAttr + ']');
    loaderLayer = mergePanel.querySelector('[' + settings.mergePanelLoaderLayerAttr + ']');

    // So init can be called multile times.
    instance.destroy();

    bindEvents();
    toBodyEnd();

    if (settings.usePanelPlaceholder) {
        createPanelPlaceholder();
    }
};

instance.destroy = () => {

    unbindEvents();

    if (settings.usePanelPlaceholder) {
        removePanelPlaceholder();
    }
};

instance.addPersonTile = (personId, htmlContent) => {
    let tmpNode = document.createElement('div'),
        trigger = document.querySelector('[' + settings.mergePanelAddPersonAttr + '="' + personId + '"]');

    // Make sure id is always of type string
    personId = personId.toString();

    // Check if person-tile is already in list
    if (personIDs.indexOf(personId) > -1) {

        // Person is in list -> abort.
        return false;
    }

    personIDs.push(personId);

    // Set selected state for trigger
    trigger.classList.add(settings.selectedClass);

    tmpNode.innerHTML = htmlContent;
    personTilesList.prepend(tmpNode.firstElementChild);
    setMergeButtonState();

    requestAnimationFrame(coverModes.refresh);
};

instance.showConfirmation = () => {
    loaderLayer.classList.remove(settings.loadingClass);

    mergePanel.setAttribute(settings.mergePanelViewAttr, 'merge-confirmation');
};

export default instance;

// Export forms object to window
window.DS = window.DS || {};
window.DS.mergePanel = instance;
